import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { practiceUsersApi } from "apis";
import { useToast } from "contexts/ToastContext";
import { useCallback } from "react";

interface DeletePracticeUserModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  onDeleteSuccess: () => void;
}

const DeletePracticeUserModal = ({
  open,
  onClose,
  userId,
  userName,
  onDeleteSuccess,
}: DeletePracticeUserModalProps) => {
  const { showSuccess, showError } = useToast();

  const deleteUserMutation = useMutation({
    mutationFn: () => practiceUsersApi.deletePracticeUser({ userId }),
    onSuccess: () => {
      showSuccess(
        "User deleted successfully",
        `${userName} has been removed from the practice.`
      );
      onDeleteSuccess();
      onClose();
    },
    onError: (error) => {
      console.error("Failed to delete practice user:", error);
      showError(
        "Failed to delete user",
        "An error occurred while deleting the user. Please try again."
      );
    },
  });

  const handleDelete = useCallback(() => {
    deleteUserMutation.mutate();
  }, [deleteUserMutation]);

  const handleClose = useCallback(() => {
    if (!deleteUserMutation.isPending) {
      onClose();
    }
  }, [deleteUserMutation, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          Delete Staff
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Are you sure you want to delete <b>{userName}</b> from the practice?
          This action cannot be undone.
        </Typography>
      </DialogContent>

      <DialogActions className="p-4 gap-2">
        <Button
          onClick={handleClose}
          disabled={deleteUserMutation.isPending}
          variant="outlined"
          className="px-6 py-2 !rounded-full"
        >
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          disabled={deleteUserMutation.isPending}
          variant="contained"
          className="px-6 py-2 !rounded-full"
          sx={{
            backgroundColor: "#DC2626",
            "&:hover": {
              backgroundColor: "#B91C1C",
            },
          }}
        >
          {deleteUserMutation.isPending ? (
            <>
              <CircularProgress size={16} sx={{ marginRight: "8px" }} />
              Deleting...
            </>
          ) : (
            "Delete"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeletePracticeUserModal;
